/**
 * A small, deliberately legible safe area — the only space in the game whose
 * whole plan you can hold in your head. Four side rooms off a colonnaded
 * atrium, every light working. It exists so the levels either side of it feel
 * worse by comparison.
 */

import { Cell } from '../../core/types';
import { scatterDamp } from './decorate';
import { addRegion, fillRect, inset, pillarLattice, strokeRect, type GenSite, type Rect } from './site';

/** Carve the hub: an atrium with four wings. Small, symmetrical, safe. */
export function generateHub(site: GenSite): void {
  const { grid, rng } = site;
  const cx = grid.w >> 1;
  const cy = grid.h >> 1;

  const atrium: Rect = { x: cx - 8, y: cy - 8, w: 17, h: 17 };
  strokeRect(grid, atrium, Cell.Wall);
  fillRect(grid, inset(atrium, 1), Cell.Floor);
  pillarLattice(grid, atrium, 4, 4);
  addRegion(site, atrium, 'atrium');

  // Four wings, each a corridor to a room with a store cupboard off it.
  const dirs: ReadonlyArray<readonly [number, number]> = [
    [0, -1],
    [0, 1],
    [1, 0],
    [-1, 0],
  ];
  for (const [dx, dy] of dirs) {
    const corridorLen = rng.int(5, 9);
    const roomW = rng.int(9, 13);
    const roomH = rng.int(9, 13);

    // Corridor out of the atrium wall.
    const startX = cx + dx * 8;
    const startY = cy + dy * 8;
    for (let k = 0; k <= corridorLen; k++) {
      const x = startX + dx * k;
      const y = startY + dy * k;
      grid.set(x, y, k === 0 ? Cell.Doorway : Cell.Floor);
      if (dx !== 0) grid.set(x, y + 1, k === 0 ? Cell.Doorway : Cell.Floor);
      else grid.set(x + 1, y, k === 0 ? Cell.Doorway : Cell.Floor);
    }

    const endX = startX + dx * corridorLen;
    const endY = startY + dy * corridorLen;
    const room: Rect = {
      // Clamped so a small hub grid cannot push a wing off the map.
      x: clampSpan(dx === 0 ? endX - (roomW >> 1) : dx > 0 ? endX : endX - roomW + 1, roomW, grid.w),
      y: clampSpan(dy === 0 ? endY - (roomH >> 1) : dy > 0 ? endY : endY - roomH + 1, roomH, grid.h),
      w: roomW,
      h: roomH,
    };
    strokeRect(grid, room, Cell.Wall);
    fillRect(grid, inset(room, 1), Cell.Floor);
    // Reopen where the corridor meets the room.
    grid.set(endX, endY, Cell.Doorway);
    if (dx !== 0) grid.set(endX, endY + 1, Cell.Doorway);
    else grid.set(endX + 1, endY, Cell.Doorway);
    addRegion(site, room, 'room');

    if (rng.chance(0.7)) {
      const cw = rng.int(2, 3);
      const ch = rng.int(2, 3);
      const closet: Rect = { x: room.x + 2, y: room.y + 2, w: cw, h: ch };
      strokeRect(grid, inset(closet, -1), Cell.Wall);
      fillRect(grid, closet, Cell.Floor);
      grid.set(closet.x + cw, closet.y, Cell.Doorway);
      addRegion(site, closet, 'closet');
    }
  }

  scatterDamp(site, rng.int(2, 5), 3, 8);
}

function clampSpan(v: number, size: number, limit: number): number {
  const max = limit - 1 - size;
  return v < 1 ? 1 : v > max ? max : v;
}
